export type ProductLabelRowFields = {
  lot: string
  serial: string
  mfg: string
  exp: string
  gtin: string
  tagid: string
}

export type ProductLabelValidationErrors = Partial<Record<keyof ProductLabelRowFields, string>>

const PRODUCT_LABEL_FIELDS_COUNT = 6
const DATE_REGEX = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(\d{4})$/
const GS1_GROUP_SEPARATOR = String.fromCharCode(29)
const GS1_FIXED_LENGTH_AIS: Partial<Record<string, number>> = {
  '01': 14,
  '11': 6,
  '13': 6,
  '15': 6,
  '17': 6,
}

export const PRODUCT_LABEL_FORMAT_HINT = 'Always: LOT|SERIAL|MFG|EXP|GTIN|TAGID(9d)'

export const createEmptyProductLabel = (): ProductLabelRowFields => ({
  lot: '',
  serial: '',
  mfg: '',
  exp: '',
  gtin: '',
  tagid: '',
})

const trimField = (value: string | undefined): string => {
  return (value || '').trim()
}

const normalizeProductLabelFields = (
  fields: Partial<ProductLabelRowFields>,
): ProductLabelRowFields => ({
  ...createEmptyProductLabel(),
  lot: trimField(fields.lot),
  serial: trimField(fields.serial),
  mfg: trimField(fields.mfg),
  exp: trimField(fields.exp),
  gtin: trimField(fields.gtin),
  tagid: trimField(fields.tagid),
})

const splitToSegments = (label: string): string[] => {
  const input = typeof label === 'string' ? label : ''
  const segments = input.split('|')

  if (segments.length <= PRODUCT_LABEL_FIELDS_COUNT) {
    return [...segments, ...Array(PRODUCT_LABEL_FIELDS_COUNT - segments.length).fill('')]
  }

  return [
    segments[0],
    segments[1],
    segments[2],
    segments[3],
    segments[4],
    segments.slice(5).join('|'),
  ]
}

export const parseProductLabel = (label: string): ProductLabelRowFields => {
  const [lot, serial, mfg, exp, gtin, tagid] = splitToSegments(label)

  return normalizeProductLabelFields({
    lot,
    serial,
    mfg,
    exp,
    gtin,
    tagid,
  })
}

const normalizeGs1Separators = (value: string): string => {
  return String(value || '')
    .replace(/\\u001d/gi, GS1_GROUP_SEPARATOR)
    .replace(/\\x1d/gi, GS1_GROUP_SEPARATOR)
    .replace(/\x1d/g, GS1_GROUP_SEPARATOR)
}

const toUsDateFromGs1 = (value: string): string => {
  if (!value || value.length !== 6) {
    return ''
  }

  const year = value.slice(0, 2)
  const month = value.slice(2, 4)
  const day = value.slice(4, 6)
  const century = Number(year) >= 70 ? '19' : '20'

  return `${month}/${day}/${century}${year}`
}

const setGs1FieldValue = (fields: ProductLabelRowFields, ai: string, rawValue: string) => {
  if (ai === '01') {
    fields.gtin = rawValue.slice(0, 14)
  }

  if (ai === '10') {
    fields.lot = rawValue
  }

  if (ai === '11') {
    fields.mfg = toUsDateFromGs1(rawValue.slice(0, 6))
  }

  if (ai === '17') {
    fields.exp = toUsDateFromGs1(rawValue.slice(0, 6))
  }

  if (ai === '21') {
    fields.serial = rawValue
  }
}

const hasSupportedGs1Values = (fields: ProductLabelRowFields): boolean => {
  return Boolean(fields.gtin || fields.lot || fields.mfg || fields.exp || fields.serial)
}

const looksLikeGs1Payload = (value: string): boolean => {
  const normalized = trimField(value)

  if (!normalized) {
    return false
  }

  return (
    /\(\d{2,4}\)/.test(normalized) ||
    normalized.includes(GS1_GROUP_SEPARATOR) ||
    /^(01|10|11|17|21)\d/.test(normalized)
  )
}

const parseGs1AiForm = (value: string): ProductLabelRowFields | null => {
  const fields = createEmptyProductLabel()
  const aiPattern = /\((\d{2,4})\)([^()]*)/g
  let match: RegExpExecArray | null
  let sawAiForm = false

  while ((match = aiPattern.exec(value)) !== null) {
    sawAiForm = true
    const ai = match[1]
    const rawValue = trimField(match[2].split(GS1_GROUP_SEPARATOR)[0])
    setGs1FieldValue(fields, ai, rawValue)
  }

  if (!sawAiForm || !hasSupportedGs1Values(fields)) {
    return null
  }

  return normalizeProductLabelFields(fields)
}

const parseGs1RawStream = (value: string): ProductLabelRowFields | null => {
  const fields = createEmptyProductLabel()
  let index = 0

  const readAi = () => value.slice(index, index + 2)
  const readFixedLengthValue = (length: number) => {
    const nextValue = value.slice(index, index + length)
    index += length
    return nextValue
  }

  while (index < value.length) {
    if (value[index] === GS1_GROUP_SEPARATOR || /\s/.test(value[index])) {
      index += 1
      continue
    }

    const ai = readAi()
    if (!/^\d{2}$/.test(ai)) {
      index += 1
      continue
    }

    index += 2

    const fixedLength = GS1_FIXED_LENGTH_AIS[ai]
    if (fixedLength) {
      const nextValue = readFixedLengthValue(fixedLength)
      setGs1FieldValue(fields, ai, nextValue)

      if (index < value.length && value[index] === GS1_GROUP_SEPARATOR) {
        index += 1
      }
      continue
    }

    if (ai === '10' || ai === '21') {
      const start = index

      while (index < value.length && value[index] !== GS1_GROUP_SEPARATOR) {
        index += 1
      }

      const nextValue = value.slice(start, index)

      if (ai === '21' && !fields.exp && !fields.lot) {
        const remainder = value.slice(start)
        let match = remainder.match(/^(.*?)(17\d{6})(10[0-9A-Za-z\-./* %]+)?$/)

        if (match) {
          fields.serial = match[1] || ''
          fields.exp = toUsDateFromGs1(match[2].slice(2))
          if (match[3]) {
            fields.lot = match[3].slice(2)
          }
          index = value.length
          break
        }

        match = remainder.match(/^(.*?)(10[0-9A-Za-z\-./* %]+)$/)
        if (match) {
          fields.serial = match[1] || ''
          fields.lot = match[2].slice(2)
          index = value.length
          break
        }
      }

      if (ai === '10' && !fields.exp) {
        const remainder = value.slice(start)
        const match = remainder.match(/^([0-9A-Za-z\-./* %]+?)(17\d{6})(21.+)?$/)

        if (match) {
          fields.lot = match[1]
          fields.exp = toUsDateFromGs1(match[2].slice(2))
          if (match[3]) {
            fields.serial = match[3].slice(2)
          }
          index = value.length
          break
        }
      }

      if (ai === '10') {
        fields.lot = nextValue
      }

      if (ai === '21') {
        fields.serial = nextValue
      }

      if (index < value.length && value[index] === GS1_GROUP_SEPARATOR) {
        index += 1
      }
      continue
    }

    while (index < value.length && value[index] !== GS1_GROUP_SEPARATOR) {
      index += 1
    }

    if (index < value.length && value[index] === GS1_GROUP_SEPARATOR) {
      index += 1
    }
  }

  if (!hasSupportedGs1Values(fields)) {
    return null
  }

  return normalizeProductLabelFields(fields)
}

export const parseScannedProductLabel = (
  rawValue: string,
  format?: string,
): ProductLabelRowFields => {
  const normalizedValue = normalizeGs1Separators(rawValue)
  const shouldTryGs1Parsing = format === 'data_matrix' || looksLikeGs1Payload(normalizedValue)

  if (shouldTryGs1Parsing) {
    const aiFormResult = parseGs1AiForm(normalizedValue)
    if (aiFormResult) {
      return aiFormResult
    }

    const rawStreamResult = parseGs1RawStream(normalizedValue)
    if (rawStreamResult) {
      return rawStreamResult
    }
  }

  return parseProductLabel(rawValue)
}

export const formatScannedProductLabelPreview = (rawValue: string, format?: string): string => {
  if (!rawValue) {
    return ''
  }

  if (format !== 'data_matrix') {
    return rawValue
  }

  return normalizeGs1Separators(rawValue)
    .replaceAll(GS1_GROUP_SEPARATOR, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export const parseProductLabels = (labels: string[] = []): ProductLabelRowFields[] => {
  if (!Array.isArray(labels)) {
    return []
  }

  return labels.map((label) => parseProductLabel(label || ''))
}

export const hasAnyProductLabelValue = (fields: ProductLabelRowFields): boolean => {
  return Object.values(fields).some((value) => trimField(value).length > 0)
}

export const serializeProductLabel = (fields: ProductLabelRowFields): string => {
  return [
    trimField(fields.lot),
    trimField(fields.serial),
    trimField(fields.mfg),
    trimField(fields.exp),
    trimField(fields.gtin),
    trimField(fields.tagid),
  ].join('|')
}

export const serializeProductLabels = (rows: ProductLabelRowFields[]): string[] => {
  return rows.map((row) => serializeProductLabel(row))
}

const isValidDate = (value: string): boolean => {
  const normalized = trimField(value)
  const match = normalized.match(DATE_REGEX)

  if (!match) {
    return false
  }

  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3])

  const date = new Date(year, month - 1, day)

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

export const validateProductLabel = (
  fields: ProductLabelRowFields,
): ProductLabelValidationErrors => {
  const errors: ProductLabelValidationErrors = {}
  const lot = trimField(fields.lot)
  const mfg = trimField(fields.mfg)
  const exp = trimField(fields.exp)
  const tagid = trimField(fields.tagid)

  if (!lot) {
    errors.lot = 'LOT is required'
  }

  if (!exp) {
    errors.exp = 'EXP is required'
  } else if (!isValidDate(exp)) {
    errors.exp = 'EXP must be MM/DD/YYYY'
  }

  if (mfg && !isValidDate(mfg)) {
    errors.mfg = 'MFG must be MM/DD/YYYY'
  }

  if (tagid && !/^\d{9}$/.test(tagid)) {
    errors.tagid = 'TAGID must be exactly 9 digits'
  }

  return errors
}

export const areProductLabelListsEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false
  }

  return left.every((value, index) => value === right[index])
}
